import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { BookOpenText, Sparkles, GraduationCap, Stethoscope, Heart, Activity, Layers, ClipboardCheck, Sun, Moon, Languages, Mail, MessagesSquare, LogIn, LogOut, User } from "lucide-react";
import { useFavorites } from "@/context/FavoritesContext";
import { useTheme } from "@/context/ThemeContext";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import SupportDialog from "@/components/SupportDialog";
import ContactDialog from "@/components/ContactDialog";

export default function Layout({ children }) {
  const loc = useLocation();
  const nav = useNavigate();
  const { ids: favIds } = useFavorites();
  const { theme, toggle: toggleTheme } = useTheme();
  const { t, lang, toggle: toggleLang } = useLang();
  const { user, logout } = useAuth();
  const [supportOpen, setSupportOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  const links = [
    { to: "/", label: t("nav.home"), icon: Stethoscope, testid: "nav-home" },
    { to: "/fall", label: t("nav.cases"), icon: ClipboardCheck, testid: "nav-cases" },
    { to: "/differentialdiagnos", label: t("nav.dx"), icon: Activity, testid: "nav-dx" },
    { to: "/flashcards", label: t("nav.flash"), icon: Layers, testid: "nav-flash" },
    { to: "/quiz", label: t("nav.quiz"), icon: GraduationCap, testid: "nav-quiz" },
    { to: "/assistent", label: t("nav.chat"), icon: Sparkles, testid: "nav-chat" },
    { to: "/favoriter", label: t("nav.fav"), icon: Heart, testid: "nav-fav" },
    { to: "/forum", label: t("nav.forum"), icon: MessagesSquare, testid: "nav-forum" },
  ];

  return (
    <div className="relative z-10 min-h-screen flex flex-col">
      <header className="border-b border-border/60 bg-background/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-link">
            <div className="size-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-serif text-lg font-semibold shadow-sm group-hover:rotate-3 transition-transform">
              <BookOpenText className="size-5" />
            </div>
            <div className="leading-tight">
              <div className="font-serif text-lg font-semibold tracking-tight">{t("app.title")}</div>
              <div className="text-[11px] text-muted-foreground uppercase tracking-widest">{t("app.subtitle")}</div>
            </div>
          </Link>
          <div className="flex items-center gap-1">
            <nav className="hidden md:flex items-center gap-1">
              {links.map((l) => {
                const Icon = l.icon;
                const active = loc.pathname === l.to || (l.to !== "/" && loc.pathname.startsWith(l.to));
                const showBadge = l.to === "/favoriter" && favIds.length > 0;
                return (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    data-testid={l.testid}
                    className={`relative px-2.5 py-2 rounded-md text-sm flex items-center gap-1.5 transition-colors ${
                      active ? "bg-primary text-primary-foreground" : "hover:bg-muted text-foreground"
                    }`}
                  >
                    <Icon className="size-4" />
                    <span className="hidden lg:inline">{l.label}</span>
                    {showBadge && (
                      <span className="text-[10px] font-mono bg-accent text-accent-foreground rounded-full px-1.5 py-0.5">
                        {favIds.length}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </nav>
            <button
              onClick={toggleLang}
              data-testid="toggle-lang"
              title={lang === "sv" ? "Switch to English" : "Byt till svenska"}
              className="ml-2 flex items-center gap-1 px-2.5 py-2 rounded-md text-xs font-mono uppercase tracking-wider hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
            >
              <Languages className="size-3.5" />
              {lang === "sv" ? "EN" : "SV"}
            </button>
            <button
              onClick={toggleTheme}
              data-testid="toggle-theme"
              title={theme === "light" ? t("common.theme.toDark") : t("common.theme.toLight")}
              className="p-2 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
            >
              {theme === "light" ? <Moon className="size-4" /> : <Sun className="size-4" />}
            </button>
            <button
              onClick={() => setContactOpen(true)}
              data-testid="open-contact"
              title={t("contact.open")}
              className="p-2 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
            >
              <Mail className="size-4" />
            </button>
            {user ? (
              <div className="flex items-center gap-1">
                <Link
                  to="/profil"
                  className="hidden md:inline text-xs text-muted-foreground hover:text-primary font-mono px-2"
                  title={user.email}
                  data-testid="header-user"
                >
                  {user.name}
                </Link>
                <button
                  onClick={() => { logout(); nav("/"); }}
                  data-testid="header-logout"
                  title={t("auth.logout")}
                  className="p-2 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                >
                  <LogOut className="size-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => nav("/logga-in")}
                data-testid="header-login"
                title={t("auth.login")}
                className="p-2 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
              >
                <LogIn className="size-4" />
              </button>
            )}
          </div>
        </div>
        <nav className="md:hidden flex items-center justify-around border-t border-border/50 px-1 py-1.5 overflow-x-auto">
          {links.map((l) => {
            const Icon = l.icon;
            const active = loc.pathname === l.to || (l.to !== "/" && loc.pathname.startsWith(l.to));
            return (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`m-${l.testid}`}
                className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded-md text-[10px] shrink-0 ${
                  active ? "text-primary font-medium" : "text-muted-foreground"
                }`}
              >
                <Icon className="size-4" />
                {l.label}
              </NavLink>
            );
          })}
        </nav>
      </header>
      <main className="flex-1 max-w-6xl w-full mx-auto px-5 sm:px-8 py-8 sm:py-14">{children}</main>
      <footer className="border-t border-border/60 mt-8">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6 text-xs text-muted-foreground flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <span>{t("footer.text")}</span>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSupportOpen(true)}
              data-testid="footer-support-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent/10 hover:bg-accent/20 text-accent transition-colors font-medium"
            >
              <Heart className="size-3.5 fill-accent" />
              {t("footer.support")}
            </button>
            <span className="font-mono">v0.4</span>
          </div>
        </div>
      </footer>
      <SupportDialog open={supportOpen} onOpenChange={setSupportOpen} />
      <ContactDialog open={contactOpen} onOpenChange={setContactOpen} />
    </div>
  );
}
